// Due to the following reasons, we are not using the Clipboard API yet for Sheet component copy/paste:
// 1. Most of the Clipboard API is only available in secure browser contexts (HTTPS)
//   - This makes it impossible to develop locally with http://localhost at the moment
// 2. Even with the Clipboard API, paste cannot be handled without a focused editable field
//   - This should be possible to make work, but will require a change to how the Sheet component selection / inputs work

import {
  WorkoutLayerExerciseMetricAdd,
  WorkoutLayerRemoval,
  WorkoutLayerSetAdd,
  WorkoutLayerSetMetric,
} from '@strength/db-schema';
import { Metric, WorkoutLayerDomain } from '@strength/domain';
import {
  HasWorkoutLayer,
  WorkoutExercise,
  WorkoutExerciseMetric,
  WorkoutSet,
  WorkoutSetMetric,
  SetMetricMutationData,
} from '@strength/domain-workout';
import {
  boundsAfterTargetSortOrder,
  boundsToReplaceSortOrders,
  nextSortOrder,
  Result,
  sortByFn,
  sortByOrder,
  sortOrdersWithinBounds,
} from '@strength/utils';
import { t } from 'i18next';
import { hasDisabledItem } from './group-data';
import {
  WorkoutClipboard,
  getMultiDayPasteTargets,
  getMultiDayPasteTargetsByPosition,
  getMultiDayCycleGroupPasteTargetsByPosition,
} from './workout-clipboard';
import {
  WorkoutLayerCommand,
  WorkoutLayerIdentity,
  WorkoutLayerModelName,
} from '../mutation-fns/types';
import {
  addExerciseEdits,
  addExerciseMetricEdits,
  addSetEdits,
  createWorkoutLayerEdit,
  editExerciseEdits,
  editSectionEdits,
  editSetMetricEdits,
  findWorkoutLayer,
  pasteExerciseEdits,
  pasteSectionEdits,
  pasteWorkoutEdits,
  removeExerciseEdits,
  removeExerciseMetricEdits,
  removeSetEdits,
  swapExerciseEdits,
} from '../mutation-fns/workout-draft';
import {
  BlockGroupBy,
  ExerciseGroupedData,
  ExerciseMetricGroupedData,
  SetGroupedData,
  WorkoutGroupedData,
  WorkoutWithCycleAndSession,
} from '../types';
import {
  buildResponse,
  ensureSetsMetricInExercise,
  ensureDefaultSetWithSetsMetric,
} from '../utils';

function processSelectedData(
  modelName: WorkoutLayerModelName,
  workoutLayers: HasWorkoutLayer[],
  clipboardData: WorkoutGroupedData,
  selectedData: WorkoutGroupedData,
  setsMetric: Metric,
  baseLayers?: HasWorkoutLayer[],
): WorkoutLayerCommand[] | null {
  const { identity, workoutLayer, baseLayer } = findWorkoutLayer(
    workoutLayers,
    selectedData.workoutId,
    modelName,
    baseLayers,
  );

  const maybeWorkout = exactlyOneFromGroup('Workout', selectedData);
  if (maybeWorkout) {
    const paste = pasteOnWorkout(
      workoutLayer,
      identity,
      clipboardData,
      baseLayer,
      setsMetric,
    );
    return paste;
  }

  const maybeWorkoutName = exactlyOneFromGroup('WorkoutName', selectedData);
  if (maybeWorkoutName) {
    return pasteOnWorkoutName(identity, workoutLayer, clipboardData);
  }

  const maybeAddSection = exactlyOneFromGroup('AddSection', selectedData);
  if (maybeAddSection) {
    return pasteOnAddSection(
      workoutLayer,
      identity,
      clipboardData,
      maybeAddSection,
      setsMetric,
    );
  }

  const maybeAddExercise = exactlyOneFromGroup('AddExercise', selectedData);
  if (maybeAddExercise) {
    return pasteOnAddExercise(
      identity,
      workoutLayer,
      clipboardData,
      maybeAddExercise,
      setsMetric,
    );
  }

  const maybeAddSet = exactlyOneFromGroup('AddSet', selectedData);
  if (maybeAddSet) {
    return pasteOnAddSet(
      identity,
      workoutLayer,
      clipboardData,
      maybeAddSet,
      setsMetric,
    );
  }

  const maybeSets = allSameTypesFromGroup(['Set'], selectedData);
  if (maybeSets) {
    return pasteOnSets(
      workoutLayer,
      identity,
      clipboardData,
      maybeSets,
      setsMetric,
    );
  }

  const maybeExercises = allSameTypesFromGroup(['Exercise'], selectedData);
  if (maybeExercises) {
    return pasteOnExercises(
      workoutLayer,
      identity,
      clipboardData,
      maybeExercises,
      setsMetric,
      baseLayer,
    );
  }

  const maybeSections = allSameTypesFromGroup(['Section'], selectedData);
  if (maybeSections) {
    return pasteOnSections(
      workoutLayer,
      identity,
      clipboardData,
      maybeSections,
      setsMetric,
    );
  }

  const maybeExerciseMetrics = allSameTypesFromGroup(
    ['ExerciseMetric', 'Set'],
    selectedData,
  );
  if (maybeExerciseMetrics) {
    return pasteOnExerciseMetrics(
      workoutLayer,
      baseLayer,
      identity,
      clipboardData,
      maybeExerciseMetrics,
      setsMetric,
    );
  }
  return null;
}

export function pasteCommands(
  modelName: WorkoutLayerModelName,
  workoutLayers: HasWorkoutLayer[],
  clipboardData: WorkoutGroupedData | null,
  allSelectedData: WorkoutGroupedData[],
  setsMetric: Metric | undefined,
  // to clear block layer from the athlete block
  baseLayers?: HasWorkoutLayer[],
): Result<WorkoutLayerCommand[], string> {
  const disabledItemPresent = hasDisabledItem(allSelectedData);
  // Allow paste to multiple workouts
  if (
    !clipboardData ||
    allSelectedData.length === 0 ||
    disabledItemPresent ||
    !setsMetric
  ) {
    return {
      ok: false,
      error: t('pasteOperationError'),
    };
  }

  // Handle both single and multi-workout paste
  if (allSelectedData.length === 0) {
    return {
      ok: false,
      error: t('noTargetSelected'),
    };
  }

  // Collect all commands from all selected data
  const allCommands: WorkoutLayerCommand[] = [];

  for (const selectedData of allSelectedData) {
    const commands = processSelectedData(
      modelName,
      workoutLayers,
      clipboardData,
      selectedData,
      setsMetric,
      baseLayers,
    );

    if (commands && commands.length > 0) {
      allCommands.push(...commands);
    } else {
      return {
        ok: false,
        error: t('pasteOperationError'),
      };
    }
  }

  return buildPasteResponse(allCommands);
}

function buildPasteResponse(
  commands: WorkoutLayerCommand[] | null,
): Result<WorkoutLayerCommand[], string> {
  return buildResponse(commands, t('pasteOperationError'));
}

/**
 * Shared inner loop: builds paste commands for each resolved (clipboardData,
 * targetWorkoutId) pair, remapping the originalTarget's workoutId to the
 * destination so the exact user selection (sets/metrics/sections) is preserved
 * rather than reconstructed from exercise-name matching.
 */
function buildCommandsFromPasteTargets(
  pasteTargets: Array<{
    clipboardData: WorkoutGroupedData;
    targetWorkoutId: string;
  }>,
  modelName: WorkoutLayerModelName,
  workoutLayers: HasWorkoutLayer[],
  setsMetric: Metric,
  baseLayers: HasWorkoutLayer[] | undefined,
  originalTarget: WorkoutGroupedData,
): Result<WorkoutLayerCommand[], string> {
  const allCommands: WorkoutLayerCommand[] = [];
  for (const { clipboardData, targetWorkoutId: tId } of pasteTargets) {
    const remappedTarget: WorkoutGroupedData = {
      ...originalTarget,
      workoutId: tId,
    };
    const result = pasteCommands(
      modelName,
      workoutLayers,
      clipboardData,
      [remappedTarget],
      setsMetric,
      baseLayers,
    );
    if (!result.ok) return result;
    allCommands.push(...result.value);
  }
  return { ok: true, value: allCommands };
}

/**
 * Collects paste commands for a multi-day clipboard using positional (session/
 * cycle order) matching. Used by the block builder and athlete sheet where
 * workouts are grouped by session or cycle rather than calendar date.
 *
 * - groupBy 'session': paste across consecutive cycles in the same session column
 * - groupBy 'cycle' / undefined: paste across consecutive sessions in the same cycle
 */
export function collectMultiDayPasteCommandsByPosition(
  clipboard: WorkoutClipboard,
  targetWorkoutId: string,
  workouts: WorkoutWithCycleAndSession[],
  modelName: WorkoutLayerModelName,
  workoutLayers: HasWorkoutLayer[],
  setsMetric: Metric,
  baseLayers: HasWorkoutLayer[] | undefined,
  groupBy: BlockGroupBy | undefined,
  originalTarget: WorkoutGroupedData,
): Result<WorkoutLayerCommand[], string> {
  const targetWorkout = workouts.find((w) => w.workoutId === targetWorkoutId);
  if (!targetWorkout) return { ok: true, value: [] };
  if (groupBy === 'all') return { ok: true, value: [] };

  const pasteTargets =
    groupBy === 'session'
      ? getMultiDayCycleGroupPasteTargetsByPosition(
          clipboard,
          targetWorkoutId,
          workouts,
        )
      : getMultiDayPasteTargetsByPosition(clipboard, targetWorkoutId, workouts);

  return buildCommandsFromPasteTargets(
    pasteTargets,
    modelName,
    workoutLayers,
    setsMetric,
    baseLayers,
    originalTarget,
  );
}

/**
 * Collects paste commands for a multi-day clipboard using date-based matching.
 * Used by the bowling block summary where workouts are shown in a calendar grid
 * and paste should fill consecutive calendar days from the target, crossing
 * cycle boundaries naturally.
 */
export function collectMultiDayPasteCommandsByDate(
  clipboard: WorkoutClipboard,
  targetWorkoutId: string,
  workouts: WorkoutWithCycleAndSession[],
  modelName: WorkoutLayerModelName,
  workoutLayers: HasWorkoutLayer[],
  setsMetric: Metric,
  baseLayers: HasWorkoutLayer[] | undefined,
  originalTarget: WorkoutGroupedData,
): Result<WorkoutLayerCommand[], string> {
  const targetWorkout = workouts.find((w) => w.workoutId === targetWorkoutId);
  if (!targetWorkout?.assignedDate) {
    return { ok: false, error: t('pasteOperationError') };
  }

  const pasteTargets = getMultiDayPasteTargets(
    clipboard,
    targetWorkout.assignedDate,
    workouts,
  );

  return buildCommandsFromPasteTargets(
    pasteTargets,
    modelName,
    workoutLayers,
    setsMetric,
    baseLayers,
    originalTarget,
  );
}

type WorkoutOneData<Type extends WorkoutGroupedData['data'][number]['type']> =
  Extract<WorkoutGroupedData['data'][number], { type: Type }>;

function exactlyOneFromGroup<
  Type extends WorkoutGroupedData['data'][number]['type'],
>(type: Type, { data }: WorkoutGroupedData): WorkoutOneData<Type> | null {
  if (data.length === 1 && data[0]?.type === type) {
    return data[0] as WorkoutOneData<Type>;
  }
  return null;
}

type WorkoutAllData<Type extends WorkoutGroupedData['data'][number]['type']> =
  Extract<WorkoutGroupedData['data'][number], { type: Type }>[];

function allSameTypesFromGroup<
  Type extends WorkoutGroupedData['data'][number]['type'],
>(
  types: Type[],
  { data }: WorkoutGroupedData,
  ignoreTypes: WorkoutGroupedData['data'][number]['type'][] = DEFAULT_IGNORE_TYPES,
): WorkoutAllData<Type> | null {
  const filteredData = data.filter((d) => !ignoreTypes.includes(d.type));
  if (
    filteredData.length > 0 &&
    filteredData.every((d) => {
      // Typescript non-sense
      const stringTypes: string[] = types;
      return stringTypes.includes(d.type);
    })
  ) {
    return filteredData as WorkoutAllData<Type>;
  }
  return null;
}

const DEFAULT_IGNORE_TYPES: WorkoutGroupedData['data'][number]['type'][] = [
  'AddSection',
  'AddExercise',
  'AddSet',
];

function pasteOnWorkoutName(
  identity: WorkoutLayerIdentity,
  workoutLayer: HasWorkoutLayer | undefined,
  clipboardData: WorkoutGroupedData,
): WorkoutLayerCommand[] | null {
  const workoutName = exactlyOneFromGroup('WorkoutName', clipboardData);
  if (!workoutName) {
    return null;
  }

  return [
    createWorkoutLayerEdit(
      identity,
      workoutLayer,
      [],
      workoutName.workout.name,
    ),
  ];
}

function pasteOnAddSection(
  workoutLayer: HasWorkoutLayer | undefined,
  identity: WorkoutLayerIdentity,
  clipboardData: WorkoutGroupedData,
  selection: WorkoutOneData<'AddSection'>,
  setsMetric: Metric,
): WorkoutLayerCommand[] | null {
  const sections = allSameTypesFromGroup(['Section'], clipboardData);
  if (!sections) {
    return null;
  }

  const edits = pasteSectionEdits(
    workoutLayer,
    setsMetric,
    sections,
    nextSortOrder(selection.workout.sections),
  );

  return [createWorkoutLayerEdit(identity, workoutLayer, edits)];
}

function pasteOnAddExercise(
  identity: WorkoutLayerIdentity,
  workoutLayer: HasWorkoutLayer | undefined,
  clipboardData: WorkoutGroupedData,
  selection: WorkoutOneData<'AddExercise'>,
  setsMetric: Metric,
): WorkoutLayerCommand[] | null {
  const exercises = allSameTypesFromGroup(['Exercise'], clipboardData);
  if (!exercises || !exercises[0]) {
    return null;
  }

  const newSectionId = selection.section.workoutSectionId;
  const targetSortOrder = nextSortOrder(selection.section.entries);
  const seenAlternateGroups = new Set<string>();
  const seenSupersets = new Set<string>();

  const entries = exercises.flatMap(
    ({ exercise, alternateGroup, superset }) => {
      const result = [];

      if (
        alternateGroup &&
        !seenAlternateGroups.has(alternateGroup.workoutAlternateGroupId)
      ) {
        seenAlternateGroups.add(alternateGroup.workoutAlternateGroupId);
        result.push(alternateGroup);
      }

      result.push(exercise);

      if (superset && !seenSupersets.has(superset.workoutSupersetId)) {
        seenSupersets.add(superset.workoutSupersetId);
        result.push(superset);
      }

      return result;
    },
  );

  const edits = pasteExerciseEdits(
    workoutLayer,
    setsMetric,
    entries,
    newSectionId,
    targetSortOrder,
  );
  return [createWorkoutLayerEdit(identity, workoutLayer, edits)];
}

function pasteOnAddSet(
  identity: WorkoutLayerIdentity,
  workoutLayer: HasWorkoutLayer | undefined,
  clipboardData: WorkoutGroupedData,
  selection: WorkoutOneData<'AddSet'>,
  setsMetric: Metric,
): WorkoutLayerCommand[] | null {
  const sets = allSameTypesFromGroup(['Set'], clipboardData);
  if (!sets) {
    return null;
  }

  return [
    createWorkoutLayerEdit(
      identity,
      workoutLayer,
      sets.flatMap(({ set }, i) =>
        addSetEdits(
          setsMetric,
          selection.exercise.workoutExerciseId,
          {
            sortOrder: nextSortOrder(selection.exercise.sets) + i,
          },
          set.metrics,
        ),
      ),
    ),
  ];
}

function pasteOnSets(
  workoutLayer: HasWorkoutLayer | undefined,
  identity: WorkoutLayerIdentity,
  clipboardData: WorkoutGroupedData,
  selection: SetGroupedData[],
  setsMetric: Metric,
): WorkoutLayerCommand[] | null {
  const clipboardSets = allSameTypesFromGroup(['Set'], clipboardData);
  if (!selection[0] || !clipboardSets) {
    return null;
  }
  const exercise = selection[0].exercise;

  const clipboardGridResult = toClipboardGrid(clipboardSets);
  if (!clipboardGridResult) {
    return null;
  }

  const { clipboardGrid, clipboardWidth } = clipboardGridResult;
  const { workoutExerciseGrid, nextWorkoutSetSortOrder } =
    toWorkoutExerciseGrid(exercise.sets, exercise.metrics);

  const metricBounds = getMetricBoundsOnWorkoutExerciseGrid(
    workoutExerciseGrid,
    selection[0].metrics.map((m) => m.metricId),
  );
  const setBounds = getSetBoundsOnWorkoutExerciseGrid(
    workoutExerciseGrid,
    selection.map((s) => s.set.workoutSetId),
  );
  if (!metricBounds || !setBounds) {
    return null;
  }

  const workoutLayerEdits = editsForSetsOnGrid(
    workoutLayer,
    exercise.workoutExerciseId,
    workoutExerciseGrid,
    nextWorkoutSetSortOrder,
    clipboardGrid,
    clipboardWidth,
    metricBounds,
    setBounds,
    setsMetric,
  );
  if (!workoutLayerEdits) {
    return null;
  }

  return [createWorkoutLayerEdit(identity, workoutLayer, workoutLayerEdits)];
}

function editsForSetsOnGrid(
  workoutLayer: HasWorkoutLayer | undefined,
  workoutExerciseId: string,
  workoutExerciseGrid: WorkoutExerciseGrid,
  nextWorkoutSetSortOrder: number,
  clipboardGrid: ClipboardGrid,
  clipboardWidth: number,
  { loMetricIndex, hiMetricIndex }: GridMetricBounds,
  { loSetIndex, hiSetIndex }: GridSetBounds,
  setsMetric: Metric,
): WorkoutLayerDomain.WorkoutLayerEdit[] | null {
  const gridEdits: SetMetricGridEdit[] = [];
  for (
    let setIndexOffset = loSetIndex;
    setIndexOffset <= hiSetIndex;
    setIndexOffset += clipboardGrid.length
  ) {
    for (
      let metricIndexOffset = loMetricIndex;
      metricIndexOffset <= hiMetricIndex;
      metricIndexOffset += clipboardWidth
    ) {
      const maybeEdits = setMetricEditsOnGrid(
        clipboardGrid,
        clipboardWidth,
        workoutExerciseGrid,
        setIndexOffset,
        metricIndexOffset,
        nextWorkoutSetSortOrder,
      );
      if (!maybeEdits) {
        return null;
      }
      gridEdits.push(...maybeEdits);
    }
  }

  const workoutLayerEdits: WorkoutLayerDomain.WorkoutLayerEdit[] = [];
  const workoutSetSortOrderToMetricData = new Map<
    number,
    SetMetricMutationData[]
  >();
  for (const edit of gridEdits) {
    if ('workoutSetId' in edit) {
      workoutLayerEdits.push(
        ...editSetMetricEdits(workoutLayer, edit.workoutSetId, edit.data),
      );
    } else {
      const match = workoutSetSortOrderToMetricData.get(
        edit.newWorkoutSetSortOrder,
      );
      if (match) {
        match.push(edit.data);
      } else {
        workoutSetSortOrderToMetricData.set(edit.newWorkoutSetSortOrder, [
          edit.data,
        ]);
      }
    }
  }
  for (const [
    sortOrder,
    metricData,
  ] of workoutSetSortOrderToMetricData.entries()) {
    workoutLayerEdits.push(
      ...addSetEdits(setsMetric, workoutExerciseId, { sortOrder }, metricData),
    );
  }

  return workoutLayerEdits;
}

function pasteOnExerciseMetrics(
  workoutLayer: HasWorkoutLayer | undefined,
  baseLayer: HasWorkoutLayer | undefined,
  identity: WorkoutLayerIdentity,
  clipboardData: WorkoutGroupedData,
  selection: WorkoutAllData<'ExerciseMetric' | 'Set'>,
  setsMetric: Metric,
): WorkoutLayerCommand[] | null {
  const clipboardSetsAndMetrics = allSameTypesFromGroup(
    ['ExerciseMetric', 'Set'],
    clipboardData,
  );
  if (!selection[0] || !clipboardSetsAndMetrics) {
    return null;
  }

  const exercise = selection[0].exercise;
  const clipboardMetrics = clipboardSetsAndMetrics.filter(
    (d): d is ExerciseMetricGroupedData => d.type === 'ExerciseMetric',
  );
  const clipboardSets = clipboardSetsAndMetrics.filter(
    (d): d is SetGroupedData => d.type === 'Set',
  );
  const selectionMetrics = selection.filter(
    (d): d is ExerciseMetricGroupedData => d.type === 'ExerciseMetric',
  );
  const selectionSets = selection.filter(
    (d): d is SetGroupedData => d.type === 'Set',
  );

  const edits = editsForMetricsAndSetsOnExercise(
    workoutLayer,
    baseLayer,
    exercise,
    clipboardMetrics,
    clipboardSets,
    selectionMetrics,
    selectionSets,
    setsMetric,
  );
  if (!edits) {
    return null;
  }

  return [createWorkoutLayerEdit(identity, workoutLayer, edits)];
}

function editsForMetricsAndSetsOnExercise(
  workoutLayer: HasWorkoutLayer | undefined,
  baseLayer: HasWorkoutLayer | undefined,
  exercise: WorkoutExercise,
  clipboardMetrics: ExerciseMetricGroupedData[],
  clipboardSets: SetGroupedData[],
  selectionMetrics: ExerciseMetricGroupedData[],
  selectionSets: SetGroupedData[],
  setsMetric: Metric,
): WorkoutLayerDomain.WorkoutLayerEdit[] | null {
  const workoutLayerEdits: WorkoutLayerDomain.WorkoutLayerEdit[] = [];

  const finalMetrics: { sortOrder: number; metricId: string }[] = [];
  const removedSortOrders: number[] = [];
  for (const metric of exercise.metrics) {
    if (selectionMetrics.some((m) => m.metric.metricId === metric.metricId)) {
      workoutLayerEdits.push(
        ...removeExerciseMetricEdits(
          workoutLayer,
          metric.workoutExerciseMetricId,
        ),
      );
      removedSortOrders.push(metric.sortOrder);
    } else {
      finalMetrics.push({
        sortOrder: metric.sortOrder,
        metricId: metric.metricId,
      });
    }
  }
  // After removing metrics, merge the edits so addExerciseMetricEdits
  // sees the layer without the deleted metrics
  let currentLayer = workoutLayer?.layer
    ? WorkoutLayerDomain.mergeLayerEdits(workoutLayer.layer, workoutLayerEdits)
    : undefined;
  let currentWorkoutLayer = currentLayer
    ? ({ ...workoutLayer, layer: currentLayer } as HasWorkoutLayer)
    : workoutLayer;

  const sortOrderBounds = boundsToReplaceSortOrders(
    exercise.metrics,
    removedSortOrders,
  );
  if (!sortOrderBounds) {
    return null;
  }
  const newSortOrders = sortOrdersWithinBounds(
    sortOrderBounds,
    clipboardMetrics.length,
  );

  let metricIndex = 0;
  for (const { metric } of clipboardMetrics) {
    const matchesExistingMetric = finalMetrics.some(
      (m) => m.metricId === metric.metricId,
    );

    if (matchesExistingMetric) {
      return null;
    }

    let sortOrder = newSortOrders[metricIndex];
    if (sortOrder === undefined) {
      return null;
    }
    metricIndex++;

    // Sets metric should always use the setsMetric.sortOrder (-100)
    if (metric.metricType === 'sets') {
      sortOrder = setsMetric.sortOrder;
    }

    const metricEdits = addExerciseMetricEdits(
      currentWorkoutLayer,
      baseLayer,
      setsMetric,
      exercise.workoutExerciseId,
      {
        metricId: metric.metricId,
        metricName: metric.metricName,
        metricType: metric.metricType,
        sortOrder,
      },
    );

    workoutLayerEdits.push(...metricEdits);

    // Merge the layer after each metric addition so the next iteration sees it
    currentLayer = currentLayer
      ? WorkoutLayerDomain.mergeLayerEdits(currentLayer, metricEdits)
      : undefined;
    currentWorkoutLayer = currentLayer
      ? ({ ...workoutLayer, layer: currentLayer } as HasWorkoutLayer)
      : workoutLayer;

    // Immediately preserve original values for unselected sets
    // (addExerciseMetricEdits creates empty setMetrics for ALL sets, but we only want to
    // overwrite selected sets with clipboard data later)
    const selectedSetIds = new Set(
      selectionSets.map((s) => s.set.workoutSetId),
    );
    for (const set of exercise.sets) {
      // Skip selected sets - they'll get clipboard data in step 6
      if (selectedSetIds.has(set.workoutSetId)) {
        continue;
      }

      // Find the original setMetric value for this metric on this set
      const originalSetMetric = set.metrics.find(
        (sm) => sm.metricId === metric.metricId,
      );

      // If this set had a value for this metric before, restore it
      // (otherwise leave it empty, which is correct for newly added metrics)
      if (originalSetMetric) {
        const preserveEdits = editSetMetricEdits(
          currentWorkoutLayer,
          set.workoutSetId,
          {
            metricId: metric.metricId,
            input: originalSetMetric.input,
            forCollection: originalSetMetric.forCollection,
          },
        );
        workoutLayerEdits.push(...preserveEdits);

        // Merge preservation edits too
        currentLayer = currentLayer
          ? WorkoutLayerDomain.mergeLayerEdits(currentLayer, preserveEdits)
          : undefined;
        currentWorkoutLayer = currentLayer
          ? ({ ...workoutLayer, layer: currentLayer } as HasWorkoutLayer)
          : workoutLayer;
      }
    }

    finalMetrics.push({
      sortOrder,
      metricId: metric.metricId,
    });
  }

  if (clipboardSets.length === 0) {
    return workoutLayerEdits;
  }

  const { workoutExerciseGrid, nextWorkoutSetSortOrder } =
    toWorkoutExerciseGrid(exercise.sets, finalMetrics);

  const clipboardGridResult = toClipboardGrid(clipboardSets);
  // Fallback to { loSetIndex: 0, hiSetIndex: 0 } if no sets are selected
  // to start pasting at the first set in the exercise
  // This happens if only metrics are selected
  const setBounds = getSetBoundsOnWorkoutExerciseGrid(
    workoutExerciseGrid,
    selectionSets.map((s) => s.set.workoutSetId),
  ) ?? { loSetIndex: 0, hiSetIndex: 0 };
  const metricBounds = getMetricBoundsOnWorkoutExerciseGrid(
    workoutExerciseGrid,
    clipboardMetrics.map((m) => m.metric.metricId),
  );
  if (!clipboardGridResult || !metricBounds) {
    return null;
  }

  const { clipboardGrid, clipboardWidth } = clipboardGridResult;

  // Use the merged layer so editSetMetricEdits can find the new setMetrics
  const mergedWorkoutLayer = currentLayer
    ? ({ ...workoutLayer, layer: currentLayer } as HasWorkoutLayer)
    : workoutLayer;

  const setEdits = editsForSetsOnGrid(
    mergedWorkoutLayer,
    exercise.workoutExerciseId,
    workoutExerciseGrid,
    nextWorkoutSetSortOrder,
    clipboardGrid,
    clipboardWidth,
    metricBounds,
    setBounds,
    setsMetric,
  );
  if (!setEdits) {
    return null;
  }

  // Remove excess sets from the exercise if clipboard has fewer sets
  // Sets at indices >= clipboard set count should be removed
  const excessSetRemovalEdits: WorkoutLayerDomain.WorkoutLayerEdit[] = [];
  const clipboardSetCount = clipboardGrid.length;
  const exerciseSetIds = workoutExerciseGrid.workoutSetIds;
  for (let i = clipboardSetCount; i < exerciseSetIds.length; i++) {
    const setIdToRemove = exerciseSetIds[i];
    if (setIdToRemove) {
      excessSetRemovalEdits.push(
        ...removeSetEdits(mergedWorkoutLayer, setIdToRemove),
      );
    }
  }

  const allEdits = workoutLayerEdits
    .concat(setEdits)
    .concat(excessSetRemovalEdits);

  return allEdits;
}

function pasteOnSections(
  workoutLayer: HasWorkoutLayer | undefined,
  identity: WorkoutLayerIdentity,
  clipboardData: WorkoutGroupedData,
  selection: WorkoutAllData<'Section'>,
  setsMetric: Metric,
): WorkoutLayerCommand[] | null {
  const clipboardSections = allSameTypesFromGroup(['Section'], clipboardData);
  if (!clipboardSections || !selection[0]) {
    return null;
  }

  // Legacy: rename a single empty section to match clipboard section name
  const clipboardSection = clipboardSections[0];
  if (
    selection.length !== 1 ||
    selection[0].exercises.length > 0 ||
    !clipboardSection ||
    clipboardSection.exercises.length > 0
  ) {
    return null;
  }

  return [
    createWorkoutLayerEdit(
      identity,
      workoutLayer,
      editSectionEdits(workoutLayer, selection[0].section.workoutSectionId, {
        name: clipboardSection.section.name,
      }),
    ),
  ];
}

function pasteOnExercises(
  workoutLayer: HasWorkoutLayer | undefined,
  identity: WorkoutLayerIdentity,
  clipboardData: WorkoutGroupedData,
  selection: WorkoutAllData<'Exercise'>,
  setsMetric: Metric,
  baseLayer?: HasWorkoutLayer | undefined,
): WorkoutLayerCommand[] | null {
  const clipboardExercises = allSameTypesFromGroup(['Exercise'], clipboardData);
  if (!selection[0] || !clipboardExercises) {
    return null;
  }
  const section = selection[0].section;

  // If same number of exercises in selection and clipboard, handle all as expected
  // If more exercise in clipboard, add exercises to the section in that spot (preserving order of exercises after selection)
  // If more exercises in selection, remove unmatched exercises

  const edits: WorkoutLayerDomain.WorkoutLayerEdit[] = [];
  const exerciseIndexLimit = Math.max(
    selection.length,
    clipboardExercises.length,
  );
  // TODO: Is the order or selection exercises guaranteed at all?
  let lastKeptExerciseSortOrder = selection[0].exercise.sortOrder;
  const toAddExercises: ExerciseGroupedData[] = [];
  for (
    let exerciseIndex = 0;
    exerciseIndex < exerciseIndexLimit;
    exerciseIndex++
  ) {
    const selectionExercise = selection[exerciseIndex];
    const clipboardExercise = clipboardExercises[exerciseIndex];
    if (selectionExercise && clipboardExercise) {
      lastKeptExerciseSortOrder = selectionExercise.exercise.sortOrder;
      const exerciseEdits = editsForExerciseOnExercise(
        identity.modelName,
        workoutLayer,
        clipboardExercise,
        selectionExercise,
        setsMetric,
        baseLayer,
      );
      if (!exerciseEdits) {
        return null;
      }

      edits.push(...exerciseEdits);
    } else if (!selectionExercise && clipboardExercise) {
      // Don't create edits here, because we may need to calculate the correct sortOrders below
      toAddExercises.push(clipboardExercise);
    } else if (selectionExercise && !clipboardExercise) {
      edits.push(
        ...removeExerciseEdits(
          workoutLayer,
          baseLayer,
          selectionExercise.exercise.workoutExerciseId,
        ),
      );
    }
  }

  const sortOrderBounds = boundsAfterTargetSortOrder(
    section.entries,
    lastKeptExerciseSortOrder,
  );
  const newSortOrders = sortOrdersWithinBounds(
    sortOrderBounds,
    toAddExercises.length,
  );
  let exerciseIndex = 0;
  for (const { exercise, sets } of toAddExercises) {
    const sortOrder = newSortOrders[exerciseIndex];
    if (sortOrder === undefined) {
      return null;
    }
    exerciseIndex++;

    const metricsWithSets = ensureSetsMetricInExercise(
      exercise.metrics,
      setsMetric,
    );
    const setsWithDefault = ensureDefaultSetWithSetsMetric(sets, setsMetric);
    edits.push(
      ...addExerciseEdits(
        setsMetric,
        section.workoutSectionId,
        {
          sortOrder,
          libraryExerciseId: exercise.libraryExerciseId,
          exerciseName: exercise.exerciseName,
          instruction: exercise.instruction ?? null,
          isEachSide: exercise.isEachSide ?? false,
        },
        metricsWithSets,
        setsWithDefault,
      ),
    );
  }

  return [createWorkoutLayerEdit(identity, workoutLayer, edits)];
}

// When pasting an exercise on another exercise
function editsForExerciseOnExercise(
  modelName: WorkoutLayerModelName,
  workoutLayer: HasWorkoutLayer | undefined,
  clipboardExercise: ExerciseGroupedData,
  selectionExercise: ExerciseGroupedData,
  setsMetric: Metric,
  baseLayer?: HasWorkoutLayer | undefined,
): WorkoutLayerDomain.WorkoutLayerEdit[] | null {
  // if the clipboard exercise doesn't have metrics, we can just treat this as a swap
  if (selectionExercise.includesMetrics && !clipboardExercise.includesMetrics) {
    const supersetExercise = selectionExercise.superset?.exercises.find(
      (e) =>
        e.workoutExerciseId === selectionExercise.exercise.workoutExerciseId,
    );
    return swapExerciseEdits(
      workoutLayer,
      modelName,
      setsMetric,
      selectionExercise.section.workoutSectionId,
      selectionExercise.exercise.workoutExerciseId,
      {
        libraryExerciseId: clipboardExercise.exercise.libraryExerciseId,
        exerciseName: clipboardExercise.exercise.exerciseName,
        instruction: clipboardExercise.exercise.instruction ?? null,
        sortOrder: clipboardExercise.exercise.sortOrder,
        isEachSide: clipboardExercise.exercise.isEachSide ?? false,
      },
      selectionExercise.exercise.metrics,
      selectionExercise.sets,
      supersetExercise,
    );
  }

  // if this is the athlete layer, we need to handle pasting differently
  // to create distinction from the base layer's exercise
  if (
    modelName === 'AthleteActualWorkoutLayer' ||
    modelName === 'AthleteDraftWorkoutLayer'
  ) {
    return getExerciseOnExercisePasteForAthlete(
      workoutLayer,
      clipboardExercise,
      selectionExercise,
      setsMetric,
      baseLayer,
    );
  }

  return getExerciseOnExercisePasteBaseEdits(
    workoutLayer,
    baseLayer,
    clipboardExercise,
    selectionExercise,
    setsMetric,
  );
}

const getExerciseOnExercisePasteBaseEdits = (
  workoutLayer: HasWorkoutLayer | undefined,
  baseLayer: HasWorkoutLayer | undefined,
  clipboardExercise: ExerciseGroupedData,
  selectionExercise: ExerciseGroupedData,
  setsMetric: Metric,
) => {
  const edits: WorkoutLayerDomain.WorkoutLayerEdit[] = [];

  edits.push(
    ...editExerciseEdits(
      workoutLayer,
      selectionExercise.exercise.workoutExerciseId,
      {
        libraryExerciseId: clipboardExercise.exercise.libraryExerciseId,
        exerciseName: clipboardExercise.exercise.exerciseName,
        instruction: clipboardExercise.exercise.instruction ?? null,
      },
    ),
  );

  if (!clipboardExercise.includesMetrics) {
    return edits;
  }

  const editsForMetricsAndSets = getEditsForMetricsAndSetsForExercise(
    workoutLayer,
    baseLayer,
    clipboardExercise,
    selectionExercise,
    setsMetric,
  );
  if (!editsForMetricsAndSets) {
    return null;
  }

  return edits.concat(editsForMetricsAndSets);
};

const getExerciseOnExercisePasteForAthlete = (
  workoutLayer: HasWorkoutLayer | undefined,
  clipboardExercise: ExerciseGroupedData,
  selectionExercise: ExerciseGroupedData,
  setsMetric: Metric,
  baseLayer?: HasWorkoutLayer | undefined,
) => {
  const supersetExercise = selectionExercise.superset?.exercises.find(
    (e) => e.workoutExerciseId === selectionExercise.exercise.workoutExerciseId,
  );
  const alternateExercise = selectionExercise.alternateGroup?.exercises.find(
    (e) => e.workoutExerciseId === selectionExercise.exercise.workoutExerciseId,
  );

  // For copy-paste on athlete layer, we need to remove the old exercise
  // as not to confuse the athlete exercise with the base exercise
  // since this is data is now athlete specific
  const editsForMetricsAndSets = getEditsForMetricsAndSetsForExercise(
    workoutLayer,
    baseLayer,
    clipboardExercise,
    selectionExercise,
    setsMetric,
  );

  const { sets, metrics } = getNewSetsAndMetrics(
    selectionExercise,
    editsForMetricsAndSets,
  );

  // Remove the old exercise and adding a new one with our new id
  const metricsWithSets = ensureSetsMetricInExercise(metrics, setsMetric);
  const setsWithDefault = ensureDefaultSetWithSetsMetric(sets, setsMetric);

  return swapExerciseEdits(
    workoutLayer,
    'AthleteDraftWorkoutLayer',
    setsMetric,
    selectionExercise.section.workoutSectionId,
    selectionExercise.exercise.workoutExerciseId,
    {
      sortOrder: selectionExercise.exercise.sortOrder,
      libraryExerciseId: clipboardExercise.exercise.libraryExerciseId,
      exerciseName: clipboardExercise.exercise.exerciseName,
      instruction: clipboardExercise.exercise.instruction ?? null,
      isEachSide: clipboardExercise.exercise.isEachSide ?? false,
    },
    metricsWithSets,
    setsWithDefault,
    supersetExercise,
    alternateExercise,
  );
};

const getNewSetsAndMetrics = (
  selectionExercise: ExerciseGroupedData,
  editsForMetricsAndSets: WorkoutLayerDomain.WorkoutLayerEdit[] | null,
) => {
  const setAdds = editsForMetricsAndSets?.filter(
    (edit) => edit.componentName === 'setAdds' && edit.action === 'create',
  );
  const metricAdds = editsForMetricsAndSets?.filter(
    (edit) =>
      edit.componentName === 'exerciseMetricAdds' && edit.action === 'create',
  );
  const setMetricAdds = editsForMetricsAndSets?.filter(
    (edit) => edit.componentName === 'setMetrics' && edit.action === 'create',
  );
  const setMetricUpdates = editsForMetricsAndSets?.filter(
    (edit) => edit.componentName === 'setMetrics' && edit.action === 'update',
  );
  const removals = editsForMetricsAndSets?.filter(
    (edit) => edit.componentName === 'removals',
  );

  // Get removed set and metric ids
  const removedSetIds: string[] = [];
  const removedExerciseMetricIds: string[] = [];
  removals?.forEach((edit) => {
    const removal = edit.data as WorkoutLayerRemoval;
    if (removal.entityType === 'exerciseMetric') {
      removedExerciseMetricIds.push(removal.entityId);
    } else if (removal.entityType === 'set') {
      removedSetIds.push(removal.entityId);
    }
  });

  const selectionSets = selectionExercise.exercise.sets;
  const selectionMetrics = selectionExercise.exercise.metrics;

  // remove sets/metrics from the exercise
  const remainingSets = selectionSets
    .flatMap((set) => set)
    .filter((set) => !removedSetIds.includes(set.workoutSetId));
  const remainingMetrics = selectionMetrics.filter(
    (metric) =>
      !removedExerciseMetricIds.includes(metric.workoutExerciseMetricId),
  );

  const resolvedMetrics: WorkoutExerciseMetric[] = metricAdds
    ? remainingMetrics.concat(
        metricAdds.map((edit) => edit.data as WorkoutLayerExerciseMetricAdd),
      )
    : [];
  const metricIds = resolvedMetrics.map((m) => m.metricId);

  // add new sets
  setAdds?.forEach((edit) => {
    const set = edit.data as WorkoutLayerSetAdd;
    remainingSets.push({
      ...set,
      metrics: [],
      status: 'planned',
    });
  });

  // go through all sets (including newly added ones) and populate their metrics
  const finalSets: WorkoutSet[] = [];
  remainingSets.forEach((set) => {
    // remove any set metrics we removed from the exercise
    const filteredSetMetrics = set.metrics.filter((setMetric) =>
      metricIds.includes(setMetric.metricId),
    );

    // add any new set metrics for this set
    const setMetricsToAdd: WorkoutSetMetric[] =
      setMetricAdds
        ?.filter((setMetric) => {
          const setMetricAdd = setMetric.data as WorkoutLayerSetMetric;
          return setMetricAdd.workoutSetId === set.workoutSetId;
        })
        .map((edit) => edit.data as WorkoutSetMetric) || [];

    const resolvedSetMetrics = setMetricsToAdd
      ? filteredSetMetrics.concat(setMetricsToAdd)
      : filteredSetMetrics;

    // Apply any updates from the clipboard paste (editsForSetsOnGrid)
    const setMetricsToUpdate =
      setMetricUpdates?.filter((edit: WorkoutLayerDomain.WorkoutLayerEdit) => {
        const update = edit.data as WorkoutLayerSetMetric;
        return update.workoutSetId === set.workoutSetId;
      }) || [];

    setMetricsToUpdate.forEach((edit: WorkoutLayerDomain.WorkoutLayerEdit) => {
      const update = edit.data as WorkoutLayerSetMetric;
      const existingIndex = resolvedSetMetrics.findIndex(
        (sm) => sm.metricId === update.metricId,
      );
      if (existingIndex !== -1) {
        const existing = resolvedSetMetrics[existingIndex];
        if (existing) {
          resolvedSetMetrics[existingIndex] = {
            ...existing,
            input: update.input ?? existing.input,
            forCollection: update.forCollection ?? existing.forCollection,
          };
        }
      }
    });

    finalSets.push({
      ...set,
      metrics: resolvedSetMetrics,
    });
  });

  return { sets: finalSets, metrics: resolvedMetrics };
};

const getEditsForMetricsAndSetsForExercise = (
  workoutLayer: HasWorkoutLayer | undefined,
  baseLayer: HasWorkoutLayer | undefined,
  clipboardExercise: ExerciseGroupedData,
  selectionExercise: ExerciseGroupedData,
  setsMetric: Metric,
) => {
  return editsForMetricsAndSetsOnExercise(
    workoutLayer,
    baseLayer,
    selectionExercise.exercise,
    sortByOrder(clipboardExercise.exercise.metrics).map((metric) => ({
      type: 'ExerciseMetric',
      metric,
      exercise: clipboardExercise.exercise,
      disabled: clipboardExercise.disabled,
    })),
    sortByOrder(clipboardExercise.sets).map((set) => ({
      type: 'Set',
      set,
      exercise: clipboardExercise.exercise,
      metrics: clipboardExercise.exercise.metrics,
      disabled: clipboardExercise.disabled,
    })),
    sortByOrder(selectionExercise.exercise.metrics).map((metric) => ({
      type: 'ExerciseMetric',
      metric,
      exercise: selectionExercise.exercise,
      disabled: clipboardExercise.disabled,
    })),
    sortByOrder(selectionExercise.sets).map((set) => ({
      type: 'Set',
      set,
      exercise: selectionExercise.exercise,
      metrics: selectionExercise.exercise.metrics,
      disabled: clipboardExercise.disabled,
    })),
    setsMetric,
  );
};

function pasteOnWorkout(
  workoutLayer: HasWorkoutLayer | undefined,
  identity: WorkoutLayerIdentity,
  clipboardData: WorkoutGroupedData,
  baseLayer: HasWorkoutLayer | undefined,
  setsMetric: Metric,
): WorkoutLayerCommand[] | null {
  const workoutData = exactlyOneFromGroup('Workout', clipboardData);
  if (!workoutData) {
    return null;
  }

  const { sections } = workoutData;

  const edits = pasteWorkoutEdits(
    workoutLayer,
    setsMetric,
    baseLayer,
    sections,
  );

  return [
    createWorkoutLayerEdit(
      identity,
      workoutLayer,
      edits,
      workoutData.workout.name,
    ),
  ];
}

type ClipboardGrid = {
  metricId: string;
  input: string;
  forCollection: boolean;
}[][];

// Assumes all sets belong to the same exercise and have the same metrics.
// If coming from groupSelection -> allSameTypeFromGroup with "Set"
function toClipboardGrid(
  setsData: SetGroupedData[],
): { clipboardGrid: ClipboardGrid; clipboardWidth: number } | null {
  const metrics = setsData[0]?.metrics;
  if (!metrics) {
    return null;
  }

  const sortedMetricIds = sortByOrder(metrics).map((m) => m.metricId);

  const clipboardGrid: ClipboardGrid = sortByFn(
    setsData,
    (d) => d.set.sortOrder,
  ).map(({ set }) => {
    return sortedMetricIds.map((metricId) => {
      const metric = set.metrics.find((m) => m.metricId === metricId);
      return {
        metricId,
        input: metric?.input ?? '',
        forCollection: metric?.forCollection ?? false,
      };
    });
  });

  return { clipboardGrid, clipboardWidth: sortedMetricIds.length };
}

type WorkoutExerciseGrid = {
  workoutSetIds: string[];
  metricIds: string[];
};

function toWorkoutExerciseGrid(
  exerciseSets: WorkoutSet[],
  exerciseMetrics: Pick<WorkoutExerciseMetric, 'sortOrder' | 'metricId'>[],
): {
  workoutExerciseGrid: WorkoutExerciseGrid;
  nextWorkoutSetSortOrder: number;
} {
  return {
    workoutExerciseGrid: {
      workoutSetIds: sortByOrder(exerciseSets).map((s) => s.workoutSetId),
      metricIds: sortByOrder(exerciseMetrics).map((m) => m.metricId),
    },
    nextWorkoutSetSortOrder: nextSortOrder(exerciseSets),
  };
}

type GridMetricBounds = {
  loMetricIndex: number;
  hiMetricIndex: number;
};

function getMetricBoundsOnWorkoutExerciseGrid(
  workoutExerciseGrid: WorkoutExerciseGrid,
  metricIds: string[],
): GridMetricBounds | null {
  let loMetricIndex: number | undefined = undefined;
  let hiMetricIndex: number | undefined = undefined;
  for (const metricId of metricIds) {
    const metricIndex = workoutExerciseGrid.metricIds.indexOf(metricId);
    if (metricIndex === -1) {
      return null;
    }
    loMetricIndex =
      loMetricIndex === undefined
        ? metricIndex
        : Math.min(loMetricIndex, metricIndex);
    hiMetricIndex =
      hiMetricIndex === undefined
        ? metricIndex
        : Math.max(hiMetricIndex, metricIndex);
  }

  if (loMetricIndex === undefined || hiMetricIndex === undefined) {
    return null;
  }
  return {
    loMetricIndex,
    hiMetricIndex,
  };
}

type GridSetBounds = {
  loSetIndex: number;
  hiSetIndex: number;
};

function getSetBoundsOnWorkoutExerciseGrid(
  workoutExerciseGrid: WorkoutExerciseGrid,
  workoutSetIds: string[],
): GridSetBounds | null {
  let loSetIndex: number | undefined = undefined;
  let hiSetIndex: number | undefined = undefined;
  for (const workoutSetId of workoutSetIds) {
    const setIndex = workoutExerciseGrid.workoutSetIds.indexOf(workoutSetId);
    if (setIndex === -1) {
      return null;
    }
    loSetIndex =
      loSetIndex === undefined ? setIndex : Math.min(loSetIndex, setIndex);
    hiSetIndex =
      hiSetIndex === undefined ? setIndex : Math.max(hiSetIndex, setIndex);
  }

  if (loSetIndex === undefined || hiSetIndex === undefined) {
    return null;
  }
  return {
    loSetIndex,
    hiSetIndex,
  };
}

type SetMetricGridEdit =
  | {
      workoutSetId: string;
      data: SetMetricMutationData;
    }
  | {
      newWorkoutSetSortOrder: number;
      data: SetMetricMutationData;
    };

// Maybe return null if out of bounds? Or detect out of bounds at a higher level?
function setMetricEditsOnGrid(
  clipboardGrid: ClipboardGrid,
  clipboardWidth: number,
  workoutExerciseGrid: WorkoutExerciseGrid,
  setIndexOffset: number,
  metricIndexOffset: number,
  nextWorkoutSetSortOrder: number,
): SetMetricGridEdit[] | null {
  const edits: SetMetricGridEdit[] = [];

  for (let setIndex = 0; setIndex < clipboardGrid.length; setIndex++) {
    const clipboardRow = clipboardGrid[setIndex];
    if (!clipboardRow) {
      continue;
    }
    for (let metricIndex = 0; metricIndex < clipboardWidth; metricIndex++) {
      const setMetric = clipboardRow[metricIndex];
      if (!setMetric) {
        continue;
      }

      const metricId =
        workoutExerciseGrid.metricIds[metricIndex + metricIndexOffset];
      if (!metricId) {
        return null;
      }

      const workoutSetId =
        workoutExerciseGrid.workoutSetIds[setIndex + setIndexOffset];
      // Note: If preventing metric mismatches on paste, we'd check metricId against setMetric.metricId here
      if (workoutSetId) {
        edits.push({
          workoutSetId,
          data: {
            metricId,
            input: setMetric.input,
            forCollection: setMetric.forCollection,
          },
        });
      } else {
        const newWorkoutSetSortOrder =
          nextWorkoutSetSortOrder -
          workoutExerciseGrid.workoutSetIds.length +
          setIndex +
          setIndexOffset;
        edits.push({
          newWorkoutSetSortOrder,
          data: {
            metricId,
            input: setMetric.input,
            forCollection: setMetric.forCollection,
          },
        });
      }
    }
  }

  return edits;
}
